
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import ProgressBar from 'primevue/progressbar';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import HelpMessage from '@/components/common/HelpMessage.vue';
import { Translation } from '@/types';
import { Lang, UserState } from '@/composables/User';

interface TranslationRequest {
  sourceLanguage: string;
  targetLanguages: string[];
  translation: Translation;
}

interface LanguageOption {
  label: string;
  value: string;
}

export default defineComponent({
  name: 'TranslationModal',

  components: {
    Dialog,
    Dropdown,
    Checkbox,
    ProgressBar,
    SpmButton,
    HelpMessage,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    translation: {
      type: Object as () => Translation | null,
      default: null,
    },

    defaultLanguage: {
      type: String,
      required: true,
    },

    activeLanguage: {
      type: String,
      required: true,
    },

    isBatchMode: {
      type: Boolean,
      default: false,
    },

    translatableFields: {
      type: Array as () => Translation[],
      default: () => [],
    },

    // Pilotage du mode batch par le parent
    batchModeTranslating: {
      type: Boolean,
      default: false,
    },

    batchModeProgress: {
      type: Number,
      default: 0,
    },
  },

  emits: ['update:visible', 'translate'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // États réactifs
    const selectedSourceLanguage = ref<string>('');
    const selectedTargetLanguages = ref<string[]>([]);
    const isTranslating = ref(false);
    const translationProgress = ref(0);
    const isCorrectionMode = ref(false);

    // États effectifs pour l'UI: en mode batch, on utilise les props du parent
    const uiIsTranslating = computed(() => (props.isBatchMode ? props.batchModeTranslating : isTranslating.value));
    const uiProgress = computed(() => (props.isBatchMode ? props.batchModeProgress : translationProgress.value));

    // Langues disponibles
    const availableLanguages = computed<LanguageOption[]>(() => UserState.activeShop?.langs.map((lang: Lang) => ({
      label: lang.id.toUpperCase(),
      value: lang.id,
    })) ?? []);

    // Détecter si le modèle n'a qu'une seule langue (force le mode correction)
    const isSingleLanguageModel = computed(() => availableLanguages.value.length === 1);

    // Langues cibles disponibles (exclut la langue source sélectionnée en mode normal)
    const availableTargetLanguages = computed<LanguageOption[]>(() => {
      if (isCorrectionMode.value || isSingleLanguageModel.value) {
        // En mode correction ou modèle mono-langue, on ne montre pas la section des langues cibles
        return [];
      }
      return availableLanguages.value.filter(
        (lang) => lang.value !== selectedSourceLanguage.value,
      );
    });

    // Texte à traduire
    const textToTranslate = computed(() => props.translation?.value || '');

    // Texte formaté pour l'affichage (décode les entités HTML)
    const formattedTextToTranslate = computed(() => {
      const text = textToTranslate.value;
      if (!text) return '';

      // Créer un élément temporaire pour décoder les entités HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text;
      return tempDiv.innerHTML;
    });

    // Obtenir le label d'une langue par son code
    const getLanguageLabel = (languageCode: string): string => {
      const language = availableLanguages.value.find((lang) => lang.value === languageCode);
      return language?.label || languageCode.toUpperCase();
    };

    // Libellé dynamique du CTA
    const translateCtaLabel = computed(() => {
      if (isCorrectionMode.value) {
        const source = getLanguageLabel(selectedSourceLanguage.value);
        return source ? `Corriger le contenu ${source}` : t('templateBuilder.translation.translate');
      }

      const count = selectedTargetLanguages.value.length;
      const source = getLanguageLabel(selectedSourceLanguage.value);
      if (count === 0) {
        return t('templateBuilder.translation.selectTargetLanguage');
      }
      if (count > 0 && source) {
        return t('templateBuilder.translation.translateFromToCount', { source, count });
      }
      return t('templateBuilder.translation.translate');
    });

    // Peut traduire selon le mode
    const canTranslate = computed(() => {
      const hasSourceLanguage = selectedSourceLanguage.value;

      if (isCorrectionMode.value) {
        // En mode correction, on a juste besoin d'une langue source
        if (props.isBatchMode) {
          return hasSourceLanguage && props.translatableFields.length > 0;
        }
        return hasSourceLanguage && textToTranslate.value.trim() !== '';
      }

      // Mode normal : besoin de langues cibles différentes de la source
      const hasTargetLanguages = selectedTargetLanguages.value.length > 0;
      if (props.isBatchMode) {
        return hasTargetLanguages && hasSourceLanguage && props.translatableFields.length > 0;
      }
      return hasTargetLanguages && hasSourceLanguage && textToTranslate.value.trim() !== '';
    });

    // Fonction pour tronquer le texte
    const truncateText = (text: string, maxLength: number): string => {
      if (!text) return '';
      // Supprimer les balises HTML pour l'aperçu
      const plainText = text.replace(/<[^>]*>/g, '');
      if (plainText.length <= maxLength) return plainText;
      return `${plainText.substring(0, maxLength)}...`;
    };

    // Initialiser les sélections par défaut
    const initializeDefaults = () => {
      selectedSourceLanguage.value = props.defaultLanguage;

      // Si modèle mono-langue, forcer le mode correction
      if (isSingleLanguageModel.value) {
        isCorrectionMode.value = true;
        selectedTargetLanguages.value = [selectedSourceLanguage.value];
      } else {
        // Même comportement en single et batch: pré-sélectionner toutes les cibles (toutes sauf la source)
        selectedTargetLanguages.value = availableLanguages.value
          .map((lang) => lang.value)
          .filter((value) => value !== selectedSourceLanguage.value);
      }
    };

    // Watcher pour réinitialiser quand la modale s'ouvre
    watch(() => props.visible, (newVisible) => {
      if (newVisible) {
        initializeDefaults();
        isTranslating.value = false;
        translationProgress.value = 0;
        // Ne pas reset le mode correction si c'est un modèle mono-langue
        if (!isSingleLanguageModel.value) {
          isCorrectionMode.value = false;
        }
      }
    });

    // Forcer le mode correction pour les modèles mono-langue
    watch(() => isSingleLanguageModel.value, (isSingle) => {
      if (isSingle) {
        isCorrectionMode.value = true;
      }
    }, { immediate: true });

    // Watcher pour le mode correction
    watch(isCorrectionMode, (newCorrectionMode) => {
      if (newCorrectionMode) {
        // En mode correction, la langue cible devient la même que la source
        selectedTargetLanguages.value = [selectedSourceLanguage.value];
      } else {
        // En mode normal, réinitialiser les langues cibles (exclure la source)
        selectedTargetLanguages.value = availableLanguages.value
          .map((lang) => lang.value)
          .filter((value) => value !== selectedSourceLanguage.value);
      }
    });

    // Watcher pour mettre à jour les langues cibles si la source change (mode normal uniquement)
    watch(selectedSourceLanguage, (newSource) => {
      if (isCorrectionMode.value) {
        // En mode correction, la cible suit automatiquement la source
        selectedTargetLanguages.value = [newSource];
      } else {
        // En mode normal, retirer la nouvelle langue source des langues cibles si elle y était
        selectedTargetLanguages.value = selectedTargetLanguages.value.filter(
          (lang) => lang !== newSource,
        );
      }
    });

    // Toggle d'une langue cible lors du clic sur la ligne entière
    const toggleTarget = (value: string) => {
      const idx = selectedTargetLanguages.value.indexOf(value);
      if (idx >= 0) {
        selectedTargetLanguages.value.splice(idx, 1);
      } else {
        selectedTargetLanguages.value.push(value);
      }
    };

    // Gérer la fermeture de la modale
    const handleClose = (value?: boolean) => {
      if (typeof value === 'boolean') {
        emit('update:visible', value);
      } else {
        emit('update:visible', false);
      }
    };

    // Gérer la traduction
    const handleTranslate = async () => {
      // En mode batch, props.translation est nul par conception. N'autoriser le retour anticipé
      // que si nous ne sommes PAS en mode batch et qu'il manque la traduction.
      if (!canTranslate.value || (!props.isBatchMode && !props.translation)) {
        return;
      }

      if (!props.isBatchMode) {
        isTranslating.value = true;
        translationProgress.value = 0;
      }

      // Déterminer l'objet Translation à inclure dans la requête.
      // En mode batch, utiliser le premier champ traduisible (il existe car canTranslate est vrai en batch).
      const baseTranslation = (props.isBatchMode
        ? (props.translatableFields[0] as Translation)
        : (props.translation as Translation));

      // En mode correction, les langues cibles sont automatiquement définies
      const targetLanguages = isCorrectionMode.value
        ? [selectedSourceLanguage.value]
        : selectedTargetLanguages.value;

      const translationRequest: TranslationRequest = {
        sourceLanguage: selectedSourceLanguage.value,
        targetLanguages,
        translation: baseTranslation,
      };

      try {
        // Émettre l'événement de traduction
        emit('translate', translationRequest);

        // En mode batch, le parent gère la progression et la fermeture
        if (!props.isBatchMode) {
          // Calculer le progrès basé sur le nombre de langues cibles (simulation simple)
          const totalLanguages = targetLanguages.length;
          const progressStep = 100 / Math.max(totalLanguages, 1);

          let currentProgress = 0;
          const progressInterval = setInterval(() => {
            currentProgress += progressStep;
            translationProgress.value = Math.min(currentProgress, 100);

            if (translationProgress.value >= 100) {
              clearInterval(progressInterval);
              setTimeout(() => {
                isTranslating.value = false;
                handleClose();
              }, 500);
            }
          }, 300);
        }
      } catch (error) {
        console.error('Erreur lors de la traduction:', error);
        if (!props.isBatchMode) {
          isTranslating.value = false;
          translationProgress.value = 0;
        }
      }
    };

    return {
      t,
      selectedSourceLanguage,
      selectedTargetLanguages,
      uiIsTranslating,
      uiProgress,
      availableLanguages,
      availableTargetLanguages,
      formattedTextToTranslate,
      canTranslate,
      translateCtaLabel,
      getLanguageLabel,
      truncateText,
      handleClose,
      handleTranslate,
      toggleTarget,
      isCorrectionMode,
      isSingleLanguageModel,
    };
  },
});
